import React from 'react';
import {
  Box,
  Grid,
  GridItem,
  Heading,
  Text,
  Card,
  CardBody,
  VStack,
  HStack,
  Badge,
  Flex,
  Progress,
  Icon,
  SimpleGrid,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { 
  FiFileText, 
  FiPackage, 
  FiDollarSign, 
  FiTrendingUp,
  FiAlertTriangle,
  FiCheckCircle,
  FiEye
} from 'react-icons/fi';
import { useQuery } from 'react-query';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { tr, enUS, de } from 'date-fns/locale';

const Dashboard = () => {
  const { t, currentLanguage } = useLanguage();
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');
  
  const { data: summary } = useQuery('dashboard-summary', () =>
    api.get('/api/dashboard/summary').then(res => res.data.data)
  );

  const { data: myPerms } = useQuery('me-perms', () => api.get('/api/users/permissions/me').then(r=>r.data.data), { enabled: !!user });
  const hasCEODashboard = !!(myPerms?.permissions || []).includes('CEODashboardPermission');
  const hasManagerDashboard = !!(myPerms?.permissions || []).includes('ManagerDashboardPermission');
  const { data: ceo } = useQuery(
    'dashboard-ceo',
    () => api.get('/api/dashboard/ceo').then(res => res.data.data),
    { enabled: !!hasCEODashboard }
  );
  const { data: mgr } = useQuery(
    'dashboard-manager',
    () => api.get('/api/dashboard/manager').then(res => res.data.data),
    { enabled: !!hasManagerDashboard }
  );

  const getDateLocale = () => {
    switch (currentLanguage) {
      case 'tr': return tr;
      case 'de': return de;
      default: return enUS;
    }
  };



  const recentProposals = summary?.recentProposals || [];
  const recentInvoices = summary?.recentInvoices || [];
  const lowStockItems = summary?.lowStockItems || [];

  // KPIs and chart data
  const conversionRate = summary?.conversionRate || 0;

  const topClientsData = summary?.topClients || [];

  const trendData = summary?.trend || [];



  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'green';
      case 'rejected': return 'red';
      case 'sent': return 'blue';
      case 'draft': return 'gray';
      default: return 'gray';
    }
  };

  const getInvoiceStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'green';
      case 'overdue': return 'red';
      case 'sent': return 'blue';
      case 'draft': return 'gray';
      default: return 'gray';
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="lg">{t('dashboard.title')}</Heading>
        <Text color="gray.500" fontSize="sm">
          {format(new Date(), 'EEEE, MMMM d, yyyy', { locale: getDateLocale() })}
        </Text>
      </Flex>

      {/* KPI + Charts Row */}
      <SimpleGrid minChildWidth="200px" spacing={4} mb={6}>
        <Card bg={bgColor} border="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={3}>{t('dashboard.conversion_rate')}</Heading>
            <VStack align="center" spacing={2}>
              <Text fontSize="4xl" fontWeight="bold" color="green.300">{conversionRate}%</Text>
              <Text fontSize="sm" color="gray.400">{t('dashboard.total_proposals')}: {summary?.recentProposals?.length || 0}</Text>
            </VStack>
          </CardBody>
        </Card>

        <Card bg={bgColor} border="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={3}>{t('dashboard.top_clients')}</Heading>
            <VStack align="stretch" spacing={3}>
              {/* Mini bar chart */}
              <Box h="80px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topClientsData.slice(0, 5)} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" hide />
                    <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} contentStyle={{ fontSize: '11px' }} />
                    <Bar dataKey="count" fill="#816BFF" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
              {/* Legend list */}
              <VStack align="stretch" spacing={1}>
                {topClientsData.slice(0, 5).map((client, idx) => (
                  <HStack key={idx} justify="space-between" fontSize="xs">
                    <HStack flex={1} minW={0}>
                      <Box w="6px" h="6px" borderRadius="full" bg={["#816BFF","#5D41FF","#47A3F3","#38B2AC","#ED64A6"][idx % 5]} flexShrink={0} />
                      <Text color="gray.400" isTruncated>{client.name}</Text>
                    </HStack>
                    <Text color="gray.300" fontWeight="medium" flexShrink={0}>{client.count}</Text>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          </CardBody>
        </Card>

        <Card bg={bgColor} border="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={3}>{t('dashboard.by_user')}</Heading>
            <Box h="150px">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                   <Pie data={summary?.byUser || []} dataKey="value" nameKey="name" innerRadius={30} outerRadius={55}>
                    {(summary?.byUser || []).map((_, i) => <Cell key={i} fill={["#5D41FF","#47A3F3","#38B2AC","#ED64A6","#ECC94B"][i%5]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </CardBody>
        </Card>

        <Card bg={bgColor} border="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={3}>{t('dashboard.by_department', { defaultValue: 'By Department' })}</Heading>
            <Box h="150px">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={summary?.byDepartment || []} dataKey="value" nameKey="name" innerRadius={30} outerRadius={55}>
                    {(summary?.byDepartment || []).map((_, i) => <Cell key={i} fill={["#47A3F3","#5D41FF","#38B2AC","#ED64A6","#ECC94B"][i%5]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </CardBody>
        </Card>

        <Card bg={bgColor} border="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={3}>{t('dashboard.trend')}</Heading>
            <Box h="150px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <XAxis dataKey="month" tick={{ fill: '#A0AEC0', fontSize: 10 }} />
                  <YAxis hide />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#3A18FA" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardBody>
        </Card>

        {/* Quick Actions - Top Right in first row */}
        <Card bg={bgColor} border="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={4}>{t('common.actions')}</Heading>
            <VStack spacing={3} align="stretch">
              <Button
                leftIcon={<FiFileText />}
                colorScheme="blue"
                variant="outline"
                size="sm"
                justifyContent="flex-start"
                as={require('react-router-dom').Link}
                to="/proposals/new"
              >
                {t('proposals.new')}
              </Button>
              <Button
                leftIcon={<FiPackage />}
                colorScheme="green"
                variant="outline"
                size="sm"
                justifyContent="flex-start"
                as={require('react-router-dom').Link}
                to="/inventory"
              >
                {t('inventory.new_item')}
              </Button>
              <Button
                leftIcon={<FiDollarSign />}
                colorScheme="purple"
                variant="outline"
                size="sm"
                justifyContent="flex-start"
                as={require('react-router-dom').Link}
                to="/invoices"
              >
                {t('invoices.new')}
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {hasManagerDashboard && mgr && (
        <SimpleGrid minChildWidth="300px" spacing={4} mb={6}>
          <Card bg={bgColor} border="1px" borderColor={borderColor}><CardBody>
            <Heading size="sm" mb={3}>{t('dashboard.manager.craft_costs', { defaultValue: 'Estimated vs Real by craft' })}</Heading>
            <Box h="200px">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={mgr.categories || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <XAxis dataKey="category" tick={{ fontSize: 10, fill: '#A0AEC0' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#A0AEC0' }} />
                  <Tooltip />
                  <Bar dataKey="estimated" name={t('dashboard.manager.estimated',{defaultValue:'Estimated'})} fill="#47A3F3" />
                  <Bar dataKey="real" name={t('dashboard.manager.real',{defaultValue:'Real'})} fill="#ED64A6" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardBody></Card>
          <Card bg={bgColor} border="1px" borderColor={borderColor}><CardBody>
            <Heading size="sm" mb={3}>{t('dashboard.manager.diff', { defaultValue: 'Diff (Total) %' })}</Heading>
            <Text fontSize="2xl" fontWeight="bold" color={Number(mgr.diff_pct)>=0?'red.500':'green.500'}>{Number(mgr.diff_pct).toFixed(1)}%</Text>
          </CardBody></Card>
          <Card bg={bgColor} border="1px" borderColor={borderColor}><CardBody>
            <Heading size="sm" mb={3}>{t('dashboard.manager.margin', { defaultValue: 'Margin %' })}</Heading>
            <Text fontSize="2xl" fontWeight="bold">{Number(mgr.margin_pct).toFixed(1)}%</Text>
          </CardBody></Card>
        </SimpleGrid>
      )}

      {hasCEODashboard && ceo && (
        <>
          <Heading size="md" mb={3} mt={6}>{t('dashboard.ceo.title', { defaultValue: 'CEO Overview' })}</Heading>
          <SimpleGrid minChildWidth="220px" spacing={4} mb={4}>
            <Card bg={bgColor} border="1px" borderColor={borderColor}><CardBody p={4}>
              <Heading size="xs" mb={2} color="gray.600">{t('dashboard.ceo.revenue_ytd', { defaultValue: 'Revenue (YTD)' })}</Heading>
              <Text fontSize="2xl" fontWeight="bold">{formatCurrency(ceo.current_year_revenue)}</Text>
            </CardBody></Card>
            <Card bg={bgColor} border="1px" borderColor={borderColor}><CardBody p={4}>
              <Heading size="xs" mb={2} color="gray.600">{t('dashboard.ceo.avg_purchase_per_sqm', { defaultValue: 'Avg purchase / m²' })}</Heading>
              <Text fontSize="2xl" fontWeight="bold">{formatCurrency(ceo.avg_purchase_per_sqm)}<Text as="span" fontSize="sm" color="gray.500"> / m²</Text></Text>
            </CardBody></Card>
            <Card bg={bgColor} border="1px" borderColor={borderColor}><CardBody p={4}>
              <Heading size="xs" mb={2} color="gray.600">{t('dashboard.ceo.offers_ytd', { defaultValue: 'Offers (YTD)' })}</Heading>
              <Text fontSize="2xl" fontWeight="bold">{ceo.offers_this_year}</Text>
            </CardBody></Card>
            <Card bg={bgColor} border="1px" borderColor={borderColor}><CardBody p={4}>
              <Heading size="xs" mb={2} color="gray.600">{t('dashboard.ceo.conversion_rate', { defaultValue: 'Conversion rate' })}</Heading>
              <Text fontSize="2xl" fontWeight="bold" color="green.500">{ceo.conversion_rate}%</Text>
            </CardBody></Card>
          </SimpleGrid>

          <SimpleGrid minChildWidth="300px" spacing={4}>
            {/* Top clients by revenue */}
            <Card bg={bgColor} border="1px" borderColor={borderColor}><CardBody>
              <Heading size="sm" mb={3}>{t('dashboard.ceo.top_clients_by_revenue', { defaultValue: 'Top clients by revenue' })}</Heading>
              <VStack align="stretch" spacing={2}>
                {(ceo.top_clients_by_revenue || []).map((c, i) => (
                  <HStack key={i} justify="space-between">
                    <Text fontSize="sm" isTruncated>{c.name}</Text>
                    <Badge colorScheme="purple">{formatCurrency(c.revenue)}</Badge>
                  </HStack>
                ))}
              </VStack>
            </CardBody></Card>

            {/* Top suppliers by revenue */}
            <Card bg={bgColor} border="1px" borderColor={borderColor}><CardBody>
              <Heading size="sm" mb={3}>{t('dashboard.ceo.top_suppliers_by_revenue', { defaultValue: 'Top suppliers by revenue' })}</Heading>
              <VStack align="stretch" spacing={2}>
                {(ceo.top_suppliers_by_revenue || []).map((s, i) => (
                  <HStack key={i} justify="space-between">
                    <Text fontSize="sm" isTruncated>{s.name}</Text>
                    <Badge colorScheme="blue">{formatCurrency(s.revenue)}</Badge>
                  </HStack>
                ))}
              </VStack>
            </CardBody></Card>

            {/* Margin trend by year */}
            <Card bg={bgColor} border="1px" borderColor={borderColor}><CardBody>
              <Heading size="sm" mb={3}>{t('dashboard.ceo.margin_trend', { defaultValue: 'Realised margin (by year)' })}</Heading>
              <Box h="180px">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={ceo.margin_trend || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#A0AEC0' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#A0AEC0' }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="margin_pct" stroke="#47A3F3" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardBody></Card>

            {/* Expense breakdown pie */}
            <Card bg={bgColor} border="1px" borderColor={borderColor}><CardBody>
              <Heading size="sm" mb={3}>{t('dashboard.ceo.expense_breakdown', { defaultValue: 'Expenses by craft' })}</Heading>
              <Box h="180px">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={ceo.expense_by_craft || []} dataKey="cost" nameKey="category" innerRadius={40} outerRadius={70}>
                      {(ceo.expense_by_craft || []).map((_, i) => <Cell key={i} fill={["#5D41FF","#47A3F3","#38B2AC","#ED64A6","#ECC94B"][i%5]} />)}
                    </Pie>
                    <Tooltip formatter={(v)=> formatCurrency(Number(v))} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardBody></Card>

            {/* Purchase / sqm by manager */}
            <Card bg={bgColor} border="1px" borderColor={borderColor}><CardBody>
              <Heading size="sm" mb={3}>{t('dashboard.ceo.purchase_per_sqm_by_manager', { defaultValue: 'Purchase / m² by manager' })}</Heading>
              <Box h="180px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ceo.purchase_per_sqm_by_manager || []} layout="vertical" margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#A0AEC0' }} />
                    <YAxis type="category" dataKey="manager" tick={{ fontSize: 10, fill: '#A0AEC0' }} width={90} />
                    <Tooltip formatter={(v)=> formatCurrency(Number(v))} />
                    <Bar dataKey="avg" fill="#816BFF" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardBody></Card>
          </SimpleGrid>
        </>
      )}

      {/* Main Content Grid */}
      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={4} mt={6}>
        {/* Left Column - Recent Activities */}
        <GridItem>
          <VStack spacing={4} align="stretch">
            {/* Recent Proposals */}
            <Card bg={bgColor} border="1px" borderColor={borderColor}>
              <CardBody>
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size="sm">{t('dashboard.recent_proposals')}</Heading>
                  <Button size="sm" variant="ghost" rightIcon={<FiEye />}>
                    {t('common.view')}
                  </Button>
                </Flex>
                <VStack spacing={3} align="stretch">
                  {recentProposals.length > 0 ? (
                    recentProposals.map((proposal) => (
                      <Box
                        key={proposal.id}
                        p={4}
                        border="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                        _hover={{ bg: hoverBgColor }}
                        transition="all 0.2s"
                      >
                        <Flex justify="space-between" align="center">
                          <VStack align="start" spacing={1} flex={1}>
                            <Text fontWeight="medium" fontSize="sm">
                              {proposal.title}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {proposal.client_name}
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                              {format(new Date(proposal.created_at), 'MMM dd, yyyy', { locale: getDateLocale() })}
                            </Text>
                          </VStack>
                          <VStack align="end" spacing={1}>
                            <Badge
                              colorScheme={getStatusColor(proposal.status)}
                              fontSize="xs"
                            >
                              {t(`proposals.status.${proposal.status}`)}
                            </Badge>
                            <Text fontSize="sm" fontWeight="medium">
                              {formatCurrency(proposal.total_amount)}
                            </Text>
                          </VStack>
                        </Flex>
                      </Box>
                    ))
                  ) : (
                    <Text color="gray.500" textAlign="center" py={4}>
                      {t('messages.no_data')}
                    </Text>
                  )}
                </VStack>
              </CardBody>
            </Card>

            {/* Recent Invoices */}
            <Card bg={bgColor} border="1px" borderColor={borderColor}>
              <CardBody>
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size="sm">{t('dashboard.recent_invoices')}</Heading>
                  <Button size="sm" variant="ghost" rightIcon={<FiEye />}>
                    {t('common.view')}
                  </Button>
                </Flex>
                <VStack spacing={3} align="stretch">
                  {recentInvoices.length > 0 ? (
                    recentInvoices.map((invoice) => (
                      <Box
                        key={invoice.id}
                        p={4}
                        border="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                        _hover={{ bg: hoverBgColor }}
                        transition="all 0.2s"
                      >
                        <Flex justify="space-between" align="center">
                          <VStack align="start" spacing={1} flex={1}>
                            <Text fontWeight="medium" fontSize="sm">
                              {invoice.invoice_number}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {invoice.client_name}
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                              {format(new Date(invoice.created_at), 'MMM dd, yyyy', { locale: getDateLocale() })}
                            </Text>
                          </VStack>
                          <VStack align="end" spacing={1}>
                            <Badge
                              colorScheme={getInvoiceStatusColor(invoice.status)}
                              fontSize="xs"
                            >
                              {t(`invoices.status.${invoice.status}`)}
                            </Badge>
                            <Text fontSize="sm" fontWeight="medium">
                              {formatCurrency(invoice.total_amount)}
                            </Text>
                          </VStack>
                        </Flex>
                      </Box>
                    ))
                  ) : (
                    <Text color="gray.500" textAlign="center" py={4}>
                      {t('messages.no_data')}
                    </Text>
                  )}
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </GridItem>

        {/* Right Column - Alerts and Quick Actions */}
        <GridItem>
          <VStack spacing={4} align="stretch">
            {/* Quick Actions (moved to top-right) */}
            <Card bg={bgColor} border="1px" borderColor={borderColor}>
              <CardBody>
                <Heading size="sm" mb={4}>{t('common.actions')}</Heading>
                <VStack spacing={3} align="stretch">
                  <Button
                    leftIcon={<FiFileText />}
                    colorScheme="blue"
                    variant="outline"
                    size="sm"
                    justifyContent="flex-start"
                    as={require('react-router-dom').Link}
                    to="/proposals/new"
                  >
                    {t('proposals.new')}
                  </Button>
                  <Button
                    leftIcon={<FiPackage />}
                    colorScheme="green"
                    variant="outline"
                    size="sm"
                    justifyContent="flex-start"
                    as={require('react-router-dom').Link}
                    to="/inventory"
                  >
                    {t('inventory.new_item')}
                  </Button>
                  <Button
                    leftIcon={<FiDollarSign />}
                    colorScheme="purple"
                    variant="outline"
                    size="sm"
                    justifyContent="flex-start"
                    as={require('react-router-dom').Link}
                    to="/invoices"
                  >
                    {t('invoices.new')}
                  </Button>
                </VStack>
              </CardBody>
            </Card>

            {/* Low Stock Alert */}
            <Card bg={bgColor} border="1px" borderColor={borderColor}>
              <CardBody>
                <Flex align="center" mb={4}>
                  <Icon as={FiAlertTriangle} color="orange.500" mr={2} />
                  <Heading size="sm">{t('dashboard.low_stock')}</Heading>
                </Flex>
                <VStack spacing={3} align="stretch">
                  {lowStockItems.length > 0 ? (
                    lowStockItems.map((item) => (
                      <Box
                        key={item.id}
                        p={3}
                        border="1px"
                        borderColor="orange.200"
                        borderRadius="md"
                        bg="orange.50"
                      >
                        <Text fontSize="sm" fontWeight="medium">
                          {item.name}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          {t('inventory.quantity')}: {item.quantity} / {item.min_stock}
                        </Text>
                        <Progress 
                          value={(item.quantity / item.min_stock) * 100} 
                          size="sm" 
                          colorScheme="orange" 
                          mt={2}
                        />
                      </Box>
                    ))
                  ) : (
                    <Text color="gray.500" textAlign="center" fontSize="sm">
                      {t('messages.no_data')}
                    </Text>
                  )}
                </VStack>
              </CardBody>
            </Card>

            

            {/* System Status */}
            <Card bg={bgColor} border="1px" borderColor={borderColor}>
              <CardBody>
                <Heading size="sm" mb={4}>{t('common.status')}</Heading>
                <VStack spacing={3} align="stretch">
                  <Flex justify="space-between" align="center">
                    <Text fontSize="sm">{t('common.database')}</Text>
                    <Badge colorScheme="green" size="sm">
                      <Icon as={FiCheckCircle} mr={1} />
                      {t('common.online')}
                    </Badge>
                  </Flex>
                  <Flex justify="space-between" align="center">
                    <Text fontSize="sm">{t('common.api')}</Text>
                    <Badge colorScheme="green" size="sm">
                      <Icon as={FiCheckCircle} mr={1} />
                      {t('common.online')}
                    </Badge>
                  </Flex>
                  <Flex justify="space-between" align="center">
                    <Text fontSize="sm">{t('common.storage')}</Text>
                    <Badge colorScheme="blue" size="sm">
                      <Icon as={FiTrendingUp} mr={1} />
                      {t('dashboard.storage_usage', { percentage: 75, defaultValue: '75%' })}
                    </Badge>
                  </Flex>
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default Dashboard;
